import { getMatchGridTemplates, saveMatchGridTemplates } from 'app/configs/matchGridTemplates';
import { getStakesNamesTemplates, saveStakesNamesTemplates } from 'app/configs/stakesNamesTemplates';

import { CONFIGS_URLS } from '../bin/constants';
import { ResponseStatus } from './api';
import { SupportedLang } from './lang';
import { getUniversalUrl } from './lib/get-universal-url';
import type { ValueOf } from './types/tsHelpers';

export const enum FetchRequestKey {
  GRID = 'match-grid',
  NAMES = 'stakes-names',
}

declare global {
  interface Window {
    __configsVersions: {
      matchGridTemplatesVersion: number;
      stakesNamesTemplatesVersion: number;
    };
  }
}

// ---

type FetchRequestReturnType<T extends VoidFunction, K extends FetchRequestKey> = Promise<{
  data: ReturnType<T>;
  key: K;
  status: ResponseStatus;
}>;

export type ConfigsRequests = (
  | FetchRequestReturnType<typeof getMatchGridTemplates, FetchRequestKey.GRID>
  | FetchRequestReturnType<typeof getStakesNamesTemplates, FetchRequestKey.NAMES>
)[];

export function fetchConfigs(configsRequests: ConfigsRequests) {
  let status: ResponseStatus;
  return Promise
    .all(configsRequests)
    .then((parsedConfigs) => {
      parsedConfigs.forEach((config) => {
        if (config.data === null || config.status !== ResponseStatus.SUCCESS) {
          console.error(`Конфиг для ${config.key} не сохранен.`, config);
          return;
        }

        status = config.status;
        switch (config.key) {
          case FetchRequestKey.GRID:
            saveMatchGridTemplates(config.data); break;
          case FetchRequestKey.NAMES:
            saveStakesNamesTemplates(config.data); break;
        }
      });

      return { status };
    });
}

function isStakeNamesTemplate(
  template: unknown,
): template is ReturnType<typeof getStakesNamesTemplates> {
  return template !== null && template !== undefined && typeof template === 'object' && 'data' in template && 'version' in template;
}

function isConfigActual(
  key: FetchRequestKey,
  template: {
    version: number;
    data?: ReturnType<typeof getMatchGridTemplates> | ReturnType<typeof getStakesNamesTemplates>;
  } | null,
  oldVersion: number,
  lang?: SupportedLang,
) {
  switch (key) {
    case FetchRequestKey.GRID:
      return template && template.version === oldVersion;
    case FetchRequestKey.NAMES:
      if (lang && template && isStakeNamesTemplate(template)) {
        return template.version === oldVersion && lang in template.data;
      }

      return false;

    default: assertExhaustiveness(key);
  }
}

type GetTemplateFunction = () => { version: number } | null;
export function createFetchRequest<T extends GetTemplateFunction, K extends FetchRequestKey>(
  key: K,
  url: ValueOf<ReturnType<typeof CONFIGS_URLS>>,
  oldVersion: number,
  getTemplate: T,
  lang?: SupportedLang,
): FetchRequestReturnType<typeof getTemplate, K> | null {
  const template = getTemplate();

  if (isConfigActual(key, template, oldVersion, lang)) {
    return null;
  }

  return new Promise(async (res, rej) => {
    try {
      const response = await fetch(getUniversalUrl(url));
      const data = response.status === ResponseStatus.SUCCESS
        ? await response.json()
        : {
          data: {},
        };

      res({ data, key, status: response.status });
    }
    catch (error) {
      console.error(error);
      rej(error);
    }
  });
}

function initClientConfigs(initClientApplication: VoidFunction, initErrorPage: VoidFunction) {
  const configsRequests: ConfigsRequests = [];
  const lang = window.__langFromSSR;

  const matchGridUrl =  getUniversalUrl(CONFIGS_URLS().MATCH_GRID);
  const stakesNamesUrl = getUniversalUrl(CONFIGS_URLS(lang).STAKES_NAMES);

  const matchGrid = createFetchRequest(
    FetchRequestKey.GRID,
    matchGridUrl,
    window.__configsVersions.matchGridTemplatesVersion,
    getMatchGridTemplates,
  );

  const stakesNames = createFetchRequest(
    FetchRequestKey.NAMES,
    stakesNamesUrl,
    window.__configsVersions.stakesNamesTemplatesVersion,
    getStakesNamesTemplates,
    lang,
  );

  matchGrid && configsRequests.push(matchGrid);
  stakesNames && configsRequests.push(stakesNames);

  if (configsRequests.length === 0) { // Конфиги не изменились
    return initClientApplication();
  }

  // Конфиги изменились, пытаемся запросить новые
  fetchConfigs(configsRequests)
    .then(initClientApplication)
    .catch((error) => {
      console.error('Не удалось загрузить конфиги', error);
      initErrorPage();
    })
    .finally(() => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.__configsVersions = undefined;
    });
}

export default initClientConfigs;
